import { Injectable, Signal, inject } from '@angular/core';
import { QueryPagingParams } from 'mbs-ui-shared@models/Paging';
import {
  AzureLifecycleRulesRequest,
  Destination,
  DestinationAssignedResponse,
  DestinationsResponse,
  GetStorageAccountDestinationsPostBody,
  S3LifecycleRulesRequest,
  SetDestinationType,
  StorageAccountState,
  StorageDestinationListUnit
} from 'mbs-ui-shared@models/StorageAccount';
import { StorageAccountState as StorageAccountStateCode } from 'mbs-ui-shared@models/storage-accounts/StorageAccountState';
import { StorageAccountsService } from 'mbs-ui-shared@services/storage-accounts/storage-accounts.service';
import { StorageDestinationsStore } from 'mbs-ui-shared@store/storageDestinations/storageDestinations.store';
import { Observable, of } from 'rxjs';
import { finalize, map, tap } from 'rxjs/operators';

// GET /destinations serializes storageAccountState as a number, unlike v2 /list which sends the string enum —
// normalized here so every consumer keeps speaking the string enum
const toStorageAccountState = (state: StorageAccountState | StorageAccountStateCode): StorageAccountState =>
  typeof state === 'number' ? StorageAccountState[StorageAccountStateCode[state] as keyof typeof StorageAccountState] : state;

type CreateDestinationPayload = { storageAccountId: string; data: SetDestinationType; skipError?: boolean };
type EditDestinationPayload = CreateDestinationPayload & { destinationId: string };
type GetDestinationsParams = QueryPagingParams & { search?: string; orderBy?: string; immutabilityEnabled?: boolean };

@Injectable({ providedIn: 'root' })
export class StorageDestinationsFacade {
  private store = inject(StorageDestinationsStore);
  private service = inject(StorageAccountsService);

  public readonly destinations: Signal<StorageDestinationListUnit[]> = this.store.all;
  public readonly loading: Signal<boolean> = this.store.loading;
  public readonly loaded: Signal<boolean> = this.store.loaded;

  // Cached until a mutation invalidates the store; changes made by another admin or tab
  // show up only after such an invalidation or a page reload
  public load(): Observable<StorageDestinationListUnit[]> {
    if (this.loaded()) return of(this.destinations());

    this.store.setLoading(true);

    return this.fetchDestinations().pipe(
      tap((destinations) => this.store.setAll(destinations)),
      finalize(() => this.store.setLoading(false))
    );
  }

  public getOfficeDestinations(): Observable<StorageDestinationListUnit[]> {
    if (this.store.officeLoaded()) return of(this.store.office());

    return this.fetchDestinations({ onlyO365StorageTypes: true }).pipe(tap((destinations) => this.store.setOffice(destinations)));
  }

  public getStorageAccountDestinations(id: string, params?: GetDestinationsParams): Observable<DestinationsResponse> {
    return this.service.getStorageAccountDestinations(id, params);
  }

  public getStorageAccountDestinationsPost(id: string, body: GetStorageAccountDestinationsPostBody): Observable<DestinationsResponse> {
    return this.service.getStorageAccountDestinationsPost(id, body);
  }

  public getDestinationAssignedPost(id: string, body: GetStorageAccountDestinationsPostBody): Observable<DestinationAssignedResponse> {
    return this.service.getSDestinationAssignedPost(id, body);
  }

  public getDestination(storageAccountId: string, destinationId: string): Observable<Destination> {
    return this.service.getDestination(storageAccountId, destinationId);
  }

  public createDestination(payload: CreateDestinationPayload): ReturnType<StorageAccountsService['createDestination']> {
    return this.service.createDestination(payload).pipe(tap(() => this.invalidate()));
  }

  public editDestination(payload: EditDestinationPayload): Observable<null> {
    return this.service.editDestination(payload).pipe(tap(() => this.invalidate()));
  }

  public deleteDestination(storageAccountId: string, destinationId: string): Observable<unknown> {
    return this.service.deleteDestination(storageAccountId, destinationId).pipe(tap(() => this.invalidate()));
  }

  public setDestinationDefaultObjectLock(accountId: string, bucketName: string): Observable<boolean> {
    return this.service.setDestinationDefaultObjectLock(accountId, bucketName).pipe(tap(() => this.invalidate()));
  }

  public setDestinationS3LifecycleRules(accountId: string, destinationId: string, data: S3LifecycleRulesRequest): Observable<null> {
    return this.service.setDestinationS3LifecycleRules(accountId, destinationId, data).pipe(tap(() => this.invalidate()));
  }

  public setDestinationAzureLifecycleRules(accountId: string, destinationId: string, data: AzureLifecycleRulesRequest): Observable<null> {
    return this.service.setDestinationAzureLifecycleRules(accountId, destinationId, data).pipe(tap(() => this.invalidate()));
  }

  public invalidate(): void {
    this.store.invalidate();
  }

  private fetchDestinations(params?: { onlyO365StorageTypes?: boolean }): Observable<StorageDestinationListUnit[]> {
    return this.service.getAllDestinations(params).pipe(
      map((res) =>
        (res?.data ?? []).map((destination) => ({
          ...destination,
          storageAccountState: toStorageAccountState(destination.storageAccountState),
          // Region is always rendered, so a missing one has to become an explicit "NA" if the backend sends null
          region: { ...destination.region, displayName: destination.region?.displayName || 'NA' }
        }))
      )
    );
  }
}
